import { useEffect, useState } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { API, useAuth } from "../App";
import { Check, X, ShieldCheck, MapPin, Calendar, FileText, Newspaper, Mail, Users, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const KIND_LABEL = {
  realities: { label: "Realtà", icon: MapPin },
  events: { label: "Eventi", icon: Calendar },
  articles: { label: "Articoli", icon: FileText },
  news: { label: "News", icon: Newspaper },
};

export default function Admin() {
  const { user, loading } = useAuth();
  const [queue, setQueue] = useState({ realities: [], events: [], articles: [], news: [], total: 0 });
  const [active, setActive] = useState("realities");
  const [showNewsletter, setShowNewsletter] = useState(false);
  const [members, setMembers] = useState({ count: 0, users: [] });
  const [subs, setSubs] = useState({ count: 0, subscribers: [] });
  const [contacts, setContacts] = useState({ count: 0, messages: [] });

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/queue`);
      setQueue(r.data);
    } catch (e) {
      if (e.response?.status === 403) toast.error("Solo admin");
      else console.error("Failed to load admin queue:", e);
    }
  };

  const loadPeople = async () => {
    try {
      const [u, s, c] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/newsletter/subscribers`),
        axios.get(`${API}/admin/contacts`),
      ]);
      setMembers(u.data);
      setSubs(s.data);
      setContacts(c.data);
    } catch (e) {
      console.error("Failed to load admin people:", e);
    }
  };

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/admin/contacts/${id}/read`);
      loadPeople();
    } catch (e) {
      console.error("Failed to mark contact read:", e);
    }
  };

  useEffect(() => { if (user?.is_admin) { load(); loadPeople(); } }, [user]);

  if (loading) return <div className="py-32 text-center text-tc-muted italic font-serif text-xl">Un istante…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/" replace />;

  const decide = async (kind, id, action) => {
    try {
      await axios.post(`${API}/admin/${kind}/${id}/${action}`);
      toast.success(action === "approve" ? "Approvato" : "Rifiutato");
      load();
    } catch { toast.error("Errore"); }
  };

  const items = queue[active] || [];

  return (
    <main className="max-w-5xl mx-auto px-6 md:px-12 py-16" data-testid="admin-page">
      <div className="flex items-center gap-3 mb-3">
        <ShieldCheck className="w-6 h-6 text-tc-secondary" />
        <div className="text-xs tracking-[0.3em] uppercase text-tc-secondary">Solo admin</div>
      </div>
      <h1 className="font-serif text-5xl md:text-6xl text-tc-primary mb-2">Coda di moderazione</h1>
      <p className="text-tc-muted mb-6">
        {queue.total === 0 ? "Niente da approvare. Tutto in pace." : `${queue.total} contenuti in attesa.`}
      </p>

      <div className="flex flex-wrap gap-3 mb-10">
        <button
          onClick={async () => {
            try {
              await axios.post(`${API}/admin/report/send`);
              toast.success(`Report inviato a ${user.email}`);
            } catch (e) {
              toast.error(e.response?.data?.detail || "Invio fallito");
            }
          }}
          className="btn-outline inline-flex items-center gap-2 text-sm !py-2 !px-5"
          data-testid="send-report-btn"
        >
          <Mail className="w-4 h-4" /> Invia report mensile
        </button>

        <button
          onClick={() => setShowNewsletter(true)}
          className="btn-outline inline-flex items-center gap-2 text-sm !py-2 !px-5"
          data-testid="open-newsletter-btn"
        >
          <Mail className="w-4 h-4" /> Invia newsletter
        </button>

        <span className="text-xs text-tc-muted self-center">Report automatico: 1° del mese, ore 09:00 (Europa/Roma)</span>
      </div>

      <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
        {Object.entries(KIND_LABEL).map(([k, { label, icon: Icon }]) => (
          <button key={k} onClick={() => setActive(k)} data-testid={`tab-${k}`}
            className={`px-5 py-2 rounded-full text-sm transition-colors border inline-flex items-center gap-2 ${active === k ? 'bg-tc-primary text-tc-bg border-tc-primary' : 'text-tc-muted border-tc'}`}>
            <Icon className="w-4 h-4" /> {label}
            {queue[k]?.length > 0 && <span className="bg-tc-secondary text-tc-bg text-[10px] rounded-full px-2 py-0.5">{queue[k].length}</span>}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {items.length === 0 && <p className="italic text-tc-muted py-8 text-center">Nessun {KIND_LABEL[active].label.toLowerCase()} in attesa.</p>}
        {items.map(item => (
          <article key={item.id} className="bg-tc-surface rounded-2xl p-6 border border-tc/50" data-testid={`pending-${item.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="text-xs text-tc-muted mb-2">
                  Inviato da <strong>{item.created_by_name || item.author || "?"}</strong>
                </div>
                <h3 className="font-serif text-2xl text-tc-primary">{item.name || item.title}</h3>
                {item.city && <div className="text-xs text-tc-muted mt-1">{item.city} · {item.region}</div>}
                {item.date && <div className="text-xs text-tc-muted mt-1">{new Date(item.date).toLocaleDateString("it-IT")} — {item.location}</div>}
                {item.category && <div className="text-xs uppercase tracking-widest text-tc-accent mt-1">{item.category}</div>}
                <p className="text-sm text-tc-muted mt-3 line-clamp-3">
                  {item.description || item.excerpt || item.body || item.content}
                </p>
                {item.website && <a href={item.website} target="_blank" rel="noreferrer" className="text-xs text-tc-secondary underline">{item.website}</a>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => decide(active, item.id, "approve")} className="bg-tc-primary text-tc-bg p-3 rounded-full hover:opacity-90" data-testid={`approve-${item.id}`}>
                  <Check className="w-4 h-4" />
                </button>
                <button onClick={() => decide(active, item.id, "reject")} className="bg-tc-secondary text-tc-bg p-3 rounded-full hover:opacity-90" data-testid={`reject-${item.id}`}>
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* MESSAGGI CONTATTO */}
      <section className="mt-20" data-testid="admin-contacts-section">
        <div className="flex items-center gap-3 mb-6">
          <MessageCircle className="w-6 h-6 text-tc-primary" />
          <h2 className="font-serif text-3xl text-tc-primary">Messaggi ricevuti</h2>
          <span className="text-xs px-3 py-1 rounded-full bg-tc-primary text-tc-bg">{contacts.count}</span>
          {contacts.messages.filter(m => !m.read).length > 0 && (
            <span className="text-xs px-2 py-1 rounded-full bg-tc-secondary text-tc-bg uppercase tracking-widest">
              {contacts.messages.filter(m => !m.read).length} da leggere
            </span>
          )}
        </div>
        <div className="bg-tc-surface rounded-3xl border border-tc/40 overflow-hidden">
          {contacts.messages.length === 0 ? (
            <p className="p-8 italic text-tc-muted text-center">Nessun messaggio ricevuto.</p>
          ) : (
            <div className="divide-y divide-tc/30 max-h-[500px] overflow-y-auto">
              {contacts.messages.map(m => (
                <div key={m.id} className={`p-5 ${!m.read ? 'bg-tc-secondary/5' : ''}`} data-testid={`contact-msg-${m.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <strong className="text-tc-primary">{m.name}</strong>
                        <a href={`mailto:${m.email}`} className="text-xs text-tc-secondary hover:underline">{m.email}</a>
                        {!m.read && <span className="text-[10px] uppercase tracking-widest bg-tc-secondary text-tc-bg px-2 py-0.5 rounded-full">Nuovo</span>}
                      </div>
                      {m.subject && <div className="font-serif text-lg text-tc-primary mt-1">{m.subject}</div>}
                    </div>
                    <div className="text-xs text-tc-muted shrink-0">
                      {m.created_at ? new Date(m.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : ""}
                    </div>
                  </div>
                  <p className="text-sm text-tc-text whitespace-pre-line leading-relaxed mt-2">{m.message}</p>
                  <div className="mt-3 flex gap-2">
                    <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject || 'Terra Comune')}`}
                      className="text-xs text-tc-primary underline hover:no-underline" data-testid={`contact-reply-${m.id}`}>
                      Rispondi via email
                    </a>
                    {!m.read && (
                      <button onClick={() => markRead(m.id)} className="text-xs text-tc-muted hover:text-tc-primary underline" data-testid={`contact-read-${m.id}`}>
                        Segna come letto
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* MEMBRI ISCRITTI AL SITO */}
      <section className="mt-20" data-testid="admin-members-section">
        <div className="flex items-center gap-3 mb-6">
          <Users className="w-6 h-6 text-tc-primary" />
          <h2 className="font-serif text-3xl text-tc-primary">Membri iscritti</h2>
          <span className="text-xs px-3 py-1 rounded-full bg-tc-primary text-tc-bg">{members.count}</span>
        </div>
        <div className="bg-tc-surface rounded-3xl border border-tc/40 overflow-hidden">
          {members.users.length === 0 ? (
            <p className="p-8 italic text-tc-muted text-center">Nessuna iscrizione ancora.</p>
          ) : (
            <div className="divide-y divide-tc/30 max-h-[500px] overflow-y-auto">
              {members.users.map(m => (
                <div key={m.user_id} className="p-5 flex items-center gap-4 flex-wrap" data-testid={`member-row-${m.user_id}`}>
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-tc-bg border border-tc/40 shrink-0">
                    {m.picture ? <img src={m.picture} alt="" className="w-full h-full object-cover" /> :
                      <div className="w-full h-full flex items-center justify-center text-tc-primary font-serif">{m.name?.[0] || "·"}</div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-tc-primary flex items-center gap-2">
                      {m.name}
                      {m.is_admin && <span className="text-[10px] px-2 py-0.5 rounded-full bg-tc-secondary/15 text-tc-secondary uppercase tracking-widest">Admin</span>}
                    </div>
                    <div className="text-xs text-tc-muted">{m.email} {m.location && `· ${m.location}`}</div>
                  </div>
                  <div className="text-xs text-tc-muted shrink-0">
                    {m.created_at ? new Date(m.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ISCRITTI NEWSLETTER */}
      <section className="mt-16" data-testid="admin-subs-section">
        <div className="flex items-center gap-3 mb-6">
          <Mail className="w-6 h-6 text-tc-primary" />
          <h2 className="font-serif text-3xl text-tc-primary">Iscritti newsletter</h2>
          <span className="text-xs px-3 py-1 rounded-full bg-tc-primary text-tc-bg">{subs.count}</span>
        </div>
        <div className="bg-tc-surface rounded-3xl border border-tc/40 overflow-hidden">
          {subs.subscribers.length === 0 ? (
            <p className="p-8 italic text-tc-muted text-center">Nessuna iscrizione alla newsletter ancora.</p>
          ) : (
            <div className="divide-y divide-tc/30 max-h-[400px] overflow-y-auto">
              {subs.subscribers.map((s, i) => (
                <div key={`${s.email}-${i}`} className="p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`sub-row-${s.email}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-tc-primary truncate">{s.email}</div>
                    {s.name && <div className="text-xs text-tc-muted">{s.name}</div>}
                  </div>
                  <div className="text-xs text-tc-muted">
                    {s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {showNewsletter && <NewsletterModal onClose={() => setShowNewsletter(false)} />}
    </main>
  );
}

function NewsletterModal({ onClose }) {
  const [form, setForm] = useState({ subject: "", body: "" });
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(null);

  useEffect(() => {
    axios.get(`${API}/newsletter/subscribers`).then(r => setCount(r.data.count)).catch(() => setCount(0));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/newsletter/send`, form);
      toast.success(`Inviata a ${r.data.sent} iscritti${r.data.failed ? `, ${r.data.failed} falliti` : ''}`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-tc-primary/40 backdrop-blur-sm z-40 flex items-center justify-center p-4" data-testid="newsletter-modal">
      <div className="bg-tc-bg rounded-3xl max-w-2xl w-full p-8 border border-tc relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-tc-muted hover:text-tc-primary"><X /></button>
        <h2 className="font-serif text-3xl text-tc-primary mb-2">Newsletter</h2>
        <p className="text-sm text-tc-muted mb-6">{count === null ? "Carico iscritti…" : `Sarà inviata a ${count} iscritti.`}</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-tc-muted">Oggetto</label>
            <input required value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
              className="w-full bg-transparent border-b border-tc py-3 outline-none focus:border-tc-primary" data-testid="newsletter-subject" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-tc-muted">Messaggio</label>
            <textarea required rows={10} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })}
              className="w-full bg-transparent border-b border-tc py-3 outline-none focus:border-tc-primary resize-none" data-testid="newsletter-body" />
          </div>
          <button type="submit" disabled={busy || !count} className="btn-primary w-full" data-testid="newsletter-send">
            {busy ? "Invio…" : `Invia a ${count || 0} iscritti`}
          </button>
        </form>
      </div>
    </div>
  );
}
